// IMPORTS
import _ from "lodash";

// REACT
import React, { useCallback, useEffect, useRef, useState } from "react";

// MATERIAL UI
import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
} from "@mui/material";

// REDUX
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { AddressDetails, setAddress } from "src/store/features/onboarding.slice";

// COMPONENTS
import { Label } from "./SelectAccountType";
import { SearchAddress, StyledTextField } from "./styled-components/AddressFields";

// FORMIK
import { Field, useFormikContext } from "formik";

// ICONS
import { GridCheckCircleIcon } from "@mui/x-data-grid";

// UTILS
import { FormValues } from "./utils/formSchema";
import useSupportedCountries from "src/utils/hooks/useSupportedCountries";
import { useUserSlice } from "src/store/features/user.slice";

interface GoogleAutocompleteProps {
  onPlaceSelected?: (place: any) => void;
}

export const StyledEndAdorment = (props: { selected: boolean }) => {
  return (
    <InputAdornment position="end">
      <div style={{ display: props.selected ? "flex" : "none", width: "100%" }}>
        <GridCheckCircleIcon sx={{ fill: "#002035", fontSize: "16px" }} />
      </div>
    </InputAdornment>
  );
};

const GoogleAutocomplete: React.FC<GoogleAutocompleteProps> = ({ onPlaceSelected }) => {
  const [input, setInput] = useState<string>("");
  const [hide, setHide] = useState<boolean>(false);
  const [delayedInput, setDelayedInput] = useState(input);
  const { data, isFetching } = api.useLookUpAddressFromInputQuery(
    { input: delayedInput },
    {
      skip: !delayedInput || delayedInput.includes(","),
    },
  );

  const debouncedChangeHandler = useCallback(
    _.debounce((input) => setDelayedInput(input), 300),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHide(false);
    const inputVal = encodeURIComponent(e.target.value);
    setInput(inputVal);
    debouncedChangeHandler(inputVal);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setInput(suggestion.description);
    setHide(true);
    if (onPlaceSelected) {
      onPlaceSelected(suggestion);
    }
  };

  const selected = !!data?.predictions?.length;

  return (
    <>
      <Field
        as={SearchAddress}
        selected={selected}
        InputProps={{
          endAdornment: <StyledEndAdorment selected={selected} />,
        }}
        value={decodeURIComponent(input)}
        onChange={handleInputChange}
        placeholder="Search places..."
      />
      {isFetching && (
        <Box py={1}>
          <LinearProgress sx={{ height: 3 }} />
        </Box>
      )}
      {!hide && selected && (
        <List dense={true}>
          {data?.predictions.map((suggestion: any) => (
            <ListItemButton
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion.description}
            </ListItemButton>
          ))}
        </List>
      )}
    </>
  );
};

const AddressLookUpComponent: React.FC = () => {
  const [getDetails] = api.useGetPlaceDetailMutation();
  const dispatch = useAppDispatch();
  const { allCountries } = useSupportedCountries();
  const {
    address: { country: signUpCountry },
  } = useUserSlice();

  const getAddressComponent = (type: string, components: any[], abbreviated?: boolean) => {
    const component = components.find((item: any) => item.types.includes(type));
    if (abbreviated) return component ? component.short_name : "";
    return component ? component.long_name : "";
  };

  const handlePlaceSelected = (place: any) => {
    // You can further type 'place' based on the Google Places API response.
    const place_id = place.place_id as string;

    getDetails({ getPlaceDetailsDto: { placeid: place_id } })
      .unwrap()
      .then((res: any) => {
        const {
          result: { formatted_address, address_components },
        } = res;

        // prepend street number to the street should if there is one
        const street = `${getAddressComponent(
          "street_number",
          address_components,
        )} ${getAddressComponent("route", address_components)}`.trim();

        const details: AddressDetails = {
          fullAddress: formatted_address,
          country: getAddressComponent("country", address_components),
          zipCode: getAddressComponent("postal_code", address_components),
          city: getAddressComponent("locality", address_components),
          concil: getAddressComponent("administrative_area_level_2", address_components),
          street: street,
          streetNumber: getAddressComponent("street_number", address_components),
          unitNumber: getAddressComponent("subpremise", address_components),
          state: getAddressComponent("administrative_area_level_1", address_components, true),
        };

        dispatch(setAddress(details));
      });
  };

  const { values, errors } = useFormikContext<FormValues>();
  const {
    country: countryErrorMessage,
    streetAddress: streetAddressErrorMessage,
    unitApartment: unitApartmentErrorMessage,
    state: stateErrorMessage,
    postCode: postCodeErrorMessage,
    city: cityErrorMessage,
  } = errors;

  const { country, state, postCode, streetAddress, unitApartment, city } = values;

  const prevValues = useRef(values);

  useEffect(() => {
    // Update redux state on manual input after checking for pottential infinite loop
    if (
      prevValues.current.streetAddress !== values.streetAddress ||
      prevValues.current.unitApartment !== values.unitApartment ||
      prevValues.current.country !== values.country ||
      prevValues.current.state !== values.state ||
      prevValues.current.postCode !== values.postCode ||
      prevValues.current.city !== values.city
    ) {
      const details = {
        street: values.streetAddress,
        country: values.country,
        city: values.city,
        zipCode: values.postCode,
        unitNumber: values.unitApartment,
        state: values.state,
      };

      dispatch(setAddress(details));
    }

    prevValues.current = values;
  }, [values, dispatch]);

  return (
    <>
      <Grid container spacing={1} mb={1}>
        <Grid item xs={12} mb={1}>
          <GoogleAutocomplete onPlaceSelected={handlePlaceSelected} />
        </Grid>
        <Grid item xs={12} mb={1}>
          <Label text="Address" />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={allCountries}
            defaultValue={{ value: signUpCountry, label: signUpCountry }}
            value={{ value: country, label: country }}
            onChange={(event, newValue) => {
              dispatch(setAddress({ country: newValue?.label ?? "" }));
            }}
            renderInput={(params) => (
              <Field
                {...params}
                data-testid="country-input"
                placeholder="Country of Residence"
                error={!!countryErrorMessage}
                helperText={countryErrorMessage}
                name="country"
                as={StyledTextField}
                selected={!!country && !countryErrorMessage}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <StyledEndAdorment selected={!!country && !countryErrorMessage} />,
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="State"
            error={!!stateErrorMessage}
            helperText={stateErrorMessage}
            as={StyledTextField}
            name="state"
            selected={!!state && !stateErrorMessage}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!state && !stateErrorMessage} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="City"
            error={!!cityErrorMessage}
            helperText={cityErrorMessage}
            as={StyledTextField}
            name="city"
            selected={!!city && !cityErrorMessage}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!city && !cityErrorMessage} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="Postcode"
            error={!!postCodeErrorMessage}
            helperText={postCodeErrorMessage}
            as={StyledTextField}
            name="postCode"
            selected={!!postCode && !postCodeErrorMessage}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!postCode && !postCodeErrorMessage} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="Street Address"
            error={!!streetAddressErrorMessage}
            helperText={streetAddressErrorMessage}
            as={StyledTextField}
            name="streetAddress"
            selected={!!streetAddress && !streetAddressErrorMessage}
            InputProps={{
              endAdornment: (
                <StyledEndAdorment selected={!!streetAddress && !streetAddressErrorMessage} />
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="(Optional) Unit/Apartment no."
            as={StyledTextField}
            name="unitApartment"
            error={!!unitApartmentErrorMessage}
            helperText={unitApartmentErrorMessage}
            selected={!!unitApartment && !unitApartmentErrorMessage}
            InputProps={{
              endAdornment: (
                <StyledEndAdorment selected={!!unitApartment && !unitApartmentErrorMessage} />
              ),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AddressLookUpComponent;
